import currency from 'currency.js';
import { createSelector } from 'reselect';
import {
  DEFAULT_EXCHANGE_RATE,
  getAdjustedContentById,
} from 'utils/stateToState';
import { accountsByIdSelector, accountIdsByGroupIdSelector } from './accounts';
import { adjustedAccountsByIdSelector } from './accountRows';
import { consolidationsSelector } from './consolidations';
import { groupsByIdSelector } from './groups';
import {
  journalEntriesByIdSelector,
  journalEntryItemsSelector,
} from './journals';
import {
  calculateNetEr,
  calculateNetErWithAdjusted,
  periodIdsSelector,
} from './periods';
import { settingsSelector } from './settings';

const adjustedGroupsByIdSelector = (entryTypes) =>
  createSelector(
    groupsByIdSelector,
    journalEntriesByIdSelector,
    journalEntryItemsSelector,
    getAdjustedContentById('group', entryTypes)
  );

/*
 * aggFunc in some column definitions does not use value stored
 * in the group row itself in calculation so instead of using aggFunc
 * we have to add appropriate value to all ancestor group rows
 */
function addToAncestors(groupRows, ancestorGroupIds, key, value) {
  ancestorGroupIds.forEach((gId) => {
    groupRows[gId][key] = groupRows[gId][key].add(value);
  });
}

const getRows = (
  accountsById,
  groupsById,
  groupsByIdCje,
  accountsByGroup,
  periodIds,
  consolidations,
  settings
) => {
  let rows = [];
  // We're doing non-recursive DFS over all groups using stack
  // First put the root group, Financial statement, with id=0 in the stack
  // stack elements are groupPathIds i.e. arrays of ancestor group ids
  let stack = [[0]];
  // rowOrder is array of indices used as sort order, one for each tree level
  let rowOrder = [0];
  // groupRows is used to store group rows to manually calculate adjusted sum
  let groupRows = {};
  while (stack.length > 0) {
    let groupPathIds = stack.pop();
    let groupId = groupPathIds[groupPathIds.length - 1];
    // Since groupPath has to be unique for each group
    // and account and it cannot consist of complex data
    // we need to encode group or account order and id in it
    let groupDepth = groupPathIds.length;
    // add one level if needed
    if (rowOrder.length <= groupDepth) {
      rowOrder.push(0);
    }
    rowOrder[groupDepth - 1] += 1;
    let groupPath;
    groupPath = groupPathIds.map(
      (gId, i) => `${rowOrder[i]}-${gId}-${groupsById[gId].name}`
    );

    const { cgroupId, code, number, expanded } = groupsById[groupId];
    let subsidiaryId = null;
    let subPrefix = '';
    if (cgroupId !== null) {
      subsidiaryId = consolidations.cgroupsById[cgroupId].subId;
      subPrefix = `${consolidations.subsidiariesById[subsidiaryId].name} `;
    }
    let groupRow = {
      id: groupId,
      group: true,
      description: `${subPrefix}${groupsById[groupId].name}`,
      expanded,
      cgroupId,
      number,
      code,
      groupPath,
    };
    for (const [, periodId] of periodIds.entries()) {
      groupRow[`unadjusted-${periodId}`] = currency(0);
      groupRow[`adjusted-${periodId}`] = currency(0);
      groupRow[`er-${periodId}`] = DEFAULT_EXCHANGE_RATE;
      groupRow[`net-er-${periodId}`] = currency(0);
      groupRow[`cje-${periodId}`] = currency(0);
      // For all periods go through all subs and add sub column data
      for (const subId of consolidations.subsidiaryIds) {
        const subsidiary = consolidations.subsidiariesById[subId];
        const subPeriodId = subsidiary.periodsMap[periodId];
        let balance = currency(0);
        if (subId === subsidiaryId) {
          balance = currency(
            consolidations.cgroupsById[cgroupId].cfPeriods[subPeriodId]
          );
        }
        groupRow[`sub-${subId}-${periodId}`] = balance;
        addToAncestors(
          groupRows,
          groupPathIds.slice(0, -1),
          `sub-${subId}-${periodId}`,
          balance
        );
      }
      try {
        groupRow[`adjusted-${periodId}`] =
          groupsById[groupId].periods[periodId].adjusted;
      } catch (e) {
        // if the group does not have any aje and rje adjustments
      }
      try {
        groupRow[`cje-${periodId}`] =
          groupsByIdCje[groupId].periods[periodId].adjusted;
      } catch (e) {
        // if the group does not have any cje adjustments
      }
      try {
        const exchangeRateId = groupsById[groupId].exchangeRateId;
        const exchangeRate = (settings?.exchangeRates || []).find(
          (exchangeRate) => exchangeRate.id === exchangeRateId
        );
        groupRow[`er-${periodId}`] = exchangeRate
          ? exchangeRate[`er-${periodId}`]
          : DEFAULT_EXCHANGE_RATE;
      } catch (e) {
        // if the group does not have any exchange rates
      }
      try {
        groupRow[`net-er-${periodId}`] = calculateNetErWithAdjusted(
          groupRow,
          periodId
        );
      } catch (e) {
        // if the group does not have any exchange rate amounts
      }
      addToAncestors(
        groupRows,
        groupPathIds.slice(0, -1),
        `unadjusted-${periodId}`,
        currency(0)
      );
      addToAncestors(
        groupRows,
        groupPathIds.slice(0, -1),
        `adjusted-${periodId}`,
        groupRow[`adjusted-${periodId}`]
      );
      addToAncestors(
        groupRows,
        groupPathIds.slice(0, -1),
        `cje-${periodId}`,
        groupRow[`cje-${periodId}`]
      );
      addToAncestors(
        groupRows,
        groupPathIds.slice(0, -1),
        `net-er-${periodId}`,
        groupRow[`net-er-${periodId}`]
      );
    }
    rows.push(groupRow);
    groupRows[groupId] = groupRow;
    // For each account in current group add one row
    accountsByGroup[groupId].forEach((accountId) => {
      const account = accountsById[accountId];
      rowOrder[groupDepth] += 1;
      const row = {
        id: accountId,
        group: false,
        number: account.number,
        description: `${account.number} ${account.description}`,
        groupPath: [
          ...groupPath,
          `${rowOrder[groupDepth]}-${accountId}-${account.description}`,
        ],
      };
      Object.entries(account.periods).forEach(([pId, period]) => {
        row[`unadjusted-${pId}`] = period.unadjusted || currency(0);
        row[`adjusted-${pId}`] = period.adjusted || currency(0);
        const exchangeRateId = account.exchangeRateId;
        const exchangeRate = (settings?.exchangeRates || []).find(
          (exchangeRate) => exchangeRate.id === exchangeRateId
        );
        row[`er-${pId}`] = exchangeRate
          ? exchangeRate[`er-${pId}`]
          : DEFAULT_EXCHANGE_RATE;
        row[`net-er-${pId}`] = calculateNetEr(row, pId);
        if (period['er-adjusted']) {
          row[`net-er-${pId}`] = currency(row[`net-er-${pId}`]).add(
            period['er-adjusted']
          );
        }
        addToAncestors(
          groupRows,
          groupPathIds,
          `unadjusted-${pId}`,
          period.unadjusted
        );
        addToAncestors(
          groupRows,
          groupPathIds,
          `adjusted-${pId}`,
          period.adjusted
        );
        addToAncestors(
          groupRows,
          groupPathIds,
          `net-er-${pId}`,
          row[`net-er-${pId}`]
        );
      });
      rows.push(row);
    });
    // add child groups in reverse so they are popped in the right order
    groupsById[groupId].childrenIds
      .slice(0)
      .reverse()
      .forEach((id) => {
        stack.push([...groupPathIds, id]);
      });
  }
  return rows;
};

const groupRowsSelector = createSelector(
  adjustedAccountsByIdSelector(['aje', 'rje']),
  adjustedGroupsByIdSelector(['aje', 'rje']),
  adjustedGroupsByIdSelector(['cje']),
  accountIdsByGroupIdSelector,
  periodIdsSelector,
  consolidationsSelector,
  settingsSelector,
  getRows
);

export default groupRowsSelector;

const getGroupRowsWithUnmappedAccounts = (accountsById, groupRows) => {
  let rows = [...groupRows];
  rows.push({
    id: -1,
    group: true,
    cgroupId: null,
    description: 'Unassigned accounts',
    groupPath: ['2-0-Unassigned accounts'],
  });
  Object.entries(accountsById)
    .filter(([accountId, account]) => account.groupId === null)
    .forEach(([accountId, account]) => {
      accountId = parseInt(accountId, 10);
      let row = {
        id: accountId,
        group: false,
        cgroupId: null,
        number: account.number,
        description: account.description,
        groupPath: [
          '2-0-Unassigned accounts',
          `2-${accountId}-${account.description}`,
        ],
      };
      Object.keys(account.periods).forEach((k) => {
        row[`unadjusted-${k}`] = account.periods[k].unadjusted;
        // these include AJE and RJE
        row[`adjusted-${k}`] = account.periods[k].adjusted;
      });
      rows.push(row);
    });
  // If there are no unassigned accounts then omit row Unassigned accounts
  if (rows.length === groupRows.length + 1) {
    return [...groupRows];
  }
  return rows;
};

export const groupRowsWithUnmappedAccountsSelector = createSelector(
  accountsByIdSelector,
  groupRowsSelector,
  getGroupRowsWithUnmappedAccounts
);
